import environment from '@environment';
import { EndpointHelper } from 'core/services/endpoint.helper';
import { HttpClient } from 'core/services/http';
import { HypermediaObject } from 'utils/hypermedia.utils';

/* eslint-disable @typescript-eslint/no-this-alias */

export class GenexModal {
  surveyApi = new EndpointHelper(`${environment.UTILITY_API_URI}/survey`);

  _configuration: {
    parentElement: HTMLElement;
    baseUrl: string;
    sessionStorageKey: string;
    backdropClickClose: boolean;
    headers: {
      'Content-Type': string;
      Accept: string;
      'x-authorization': string;
    };
    backdropClass: string;
    modalClass: string;
    modalCloseClass: string;
    modalIFrameClass: string;
    onTriggerResultEvent: ((uniqueInteractionId: string, eventInformation: any, errors: any) => void) | null;
    onBeforeSurveyStart: ((uniqueInteractionId: string, eventInformation: any) => void) | null;
    onSurveyStarted: ((eventInformation: any) => void) | null;
    onSurveyClosed: ((uniqueInteractionId: string, eventInformation: any) => void) | null;
    onError: ((uniqueInteractionId: string, eventInformation: any, errorMessage: string) => void) | null;
    [key: string]: any;
  } = {
    // Likely a TS rule - the setDefaultConfiguration () call in the constructor should logically just do this, but apparently it needs to be explicitly set and not via a func?
    parentElement: document.getElementById('self-service-app-root') || document.body,
    baseUrl: 'genexproxy/',
    sessionStorageKey: 'genex-eventInformation-',
    backdropClickClose: true,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'x-authorization': '',
    },
    backdropClass: 'genex-backdrop',
    modalClass: 'genex-survey-modal',
    modalCloseClass: 'genex-survey-modal-close',
    modalIFrameClass: 'genex-survey-modal-iframe',
    onTriggerResultEvent: null,
    onBeforeSurveyStart: null,
    onSurveyStarted: null,
    onSurveyClosed: null,
    onError: null,
  };

  constructor() {
    //Why doesn't this work for init? I have to explicitly set the default _configuration in the constructor itself in order to avoid an error.
    this.setDefaultConfiguration();
  }

  private setDefaultConfiguration(): void {
    this._configuration = {
      parentElement: document.getElementById('self-service-app-root') || document.body,
      baseUrl: 'genexproxy/',
      sessionStorageKey: 'genex-eventInformation-',
      backdropClickClose: true,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'x-authorization': '',
      },
      backdropClass: 'genex-backdrop',
      modalClass: 'genex-survey-modal',
      modalCloseClass: 'genex-survey-modal-close',
      modalIFrameClass: 'genex-survey-modal-iframe',
      onTriggerResultEvent: null,
      onBeforeSurveyStart: null,
      onSurveyStarted: null,
      onSurveyClosed: null,
      onError: null,
    };
  }

  //Useful info:

  //When an interaction or survey is initiated, a unique uniqueInteractionId is generated. This identifier is used to create a unique sessionStorage key by combining it with
  //the sessionStorageKey.

  //The eventInformation object is created or retrieved from sessionStorage using the unique key. It's used to store and manage the state of the interaction,
  //including whether the trigger query has been performed, the survey has been started, and other relevant data.

  //Throughout the component's methods, eventInformation is updated based on the outcomes of trigger queries, survey starts, and other actions.
  //This allows the component to maintain a record of the interaction's progress.

  //When specific conditions are met (e.g. the survey is ready to start), the component uses the eventInformation to determine the appropriate course of action,
  //such as starting the survey and displaying the survey modal.

  //---------------------------------------------------------------------------------------------------------------------------------------------------------------------------

  public setConfiguration(configuration: Partial<this['_configuration']>): void {
    //Tell this class what DOM page we're on and what we want it to generate.
    if (!this._configuration) {
      this.setDefaultConfiguration();
    }

    if (!configuration) {
      console.warn('Parameter cannot be null: configuration. Default configuration used');
    }

    for (const property in this._configuration) {
      if (property in configuration) {
        this._configuration[property] = configuration[property];
      }
    }

    if (this._configuration.baseUrl.length > 1 && !this._configuration.baseUrl.endsWith('/')) {
      this._configuration.baseUrl += '/';
    }
  }

  public loadTrigger(
    //The core function to trigger the survey displaying now that all the checks have been made.
    uniqueInteractionId: string,
    payload: any,
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types
    startSurvey: boolean = false,
    customerNumber: string,
    country?: string,
    stopBeforeModel = false
  ): Promise<any> {
    const eventInformation = GenexModal._newEventInformation(
      this._configuration.sessionStorageKey,
      uniqueInteractionId
    );

    if (!eventInformation) {
      const errorMessage = `Cannot create another Event with the same unique Interaction Id ${uniqueInteractionId}`;
      console.error(errorMessage);

      if (this._configuration.onError) {
        this._configuration.onError(uniqueInteractionId, eventInformation, errorMessage);
      }

      return new Promise(() => null);
    }

    eventInformation.payload = payload;

    GenexModal._setEventInformation(this._configuration.sessionStorageKey, uniqueInteractionId, eventInformation);

    // const url = this._configuration.baseUrl + 'trigger/query';
    const url = this.surveyApi
      .createUrl()
      .addRouteParam(customerNumber)
      .addRouteParam('trigger')
      .addRouteParam('query')
      .addQueryParam('Country', country)
      .build();
    // return HttpClient.GetInstance.post<unknown, HypermediaObject>(url)
    // .then(res => HypermediaUtils.ParseResponseToObject<any>(res?.data))
    return (
      HttpClient.GetInstance.post<unknown, HypermediaObject>(url, eventInformation.payload)
        // .then(res => {
        //   console.log("response from server: ", res);
        //    const parsedData = HypermediaUtils.ParseResponseToObject<any>(res?.data)
        //    console.log("parsed response from server: ", parsedData);
        //    return parsedData;
        // })
        .then((res) => {
          return this._handleTriggerQueryResponse(uniqueInteractionId, res?.data, startSurvey, customerNumber, country, stopBeforeModel);
        })
        .catch((error) => {
          const errorMessage = `Trigger Response Error: ${JSON.stringify(error)}`;
          console.error(errorMessage);
          if (this._configuration.onError) {
            this._configuration.onError(uniqueInteractionId, eventInformation, errorMessage);
          }

          return this._handleTriggerQueryResponse(uniqueInteractionId, null, false, '', country, stopBeforeModel);
        })
    );

    // return fetch(url, {
    //   method: 'POST',
    //   cache: 'no-cache',
    //   headers: this._configuration.headers,
    //   body: JSON.stringify(eventInformation.payload)
    // })
    // .then((response) => response.json())
    // .then((data) => {
    //   console.log('Trigger Response:', data);
    //   return self._handleTriggerQueryResponse(uniqueInteractionId, data, startSurvey, '', country);
    // })
    // .catch((error) => {
    //   const errorMessage = `Trigger Response Error: ${JSON.stringify(error)}`;
    //   console.error(errorMessage);

    //   if (this._configuration.onError) {
    //     this._configuration.onError(uniqueInteractionId, eventInformation, errorMessage);
    //   }

    //   return self._handleTriggerQueryResponse(uniqueInteractionId, null, false, '', country);
    // });
  }

  public canSurvey(uniqueInteractionId: string): boolean {
    //Check the event information to make sure the survey's allowed to be initiated.
    const eventInformation = GenexModal._getEventInformation(
      this._configuration.sessionStorageKey,
      uniqueInteractionId
    );

    if (!eventInformation || !eventInformation.surveyId) {
      return false;
    }

    return !eventInformation.surveyActioned;
  }

  public startSurvey(
    uniqueInteractionId: string,
    customerNumber: string,
    payload: any = null,
    country?: string,
    stopBeforeModel = false,
  ): Promise<any> {
    //This is the first thing we call to start the survey process (after performing setConfiguration). uniqueInteractionId is just a timestamp we'll use for parity. NB: Adhere to the format of the payload.

    const eventInformation = GenexModal._getEventInformation(
      this._configuration.sessionStorageKey,
      uniqueInteractionId
    );

    if (!eventInformation) {
      return this.loadTrigger(uniqueInteractionId, payload, true, customerNumber, country, stopBeforeModel);
    }

    if (!this.canSurvey(uniqueInteractionId)) {
      const errorMessage = 'Cannot Survey';
      console.error(errorMessage);
      if (this._configuration.onError) {
        this._configuration.onError(uniqueInteractionId, eventInformation, errorMessage);
      }

      return new Promise(() => null);
    }

    if (payload) {
      eventInformation.payload = payload;
    }

    eventInformation.payload.survey_id = eventInformation.surveyId;
    eventInformation.surveyActioned = true;
    (eventInformation.payload.enable_rule_engine = true),
      GenexModal._setEventInformation(this._configuration.sessionStorageKey, uniqueInteractionId, eventInformation);

    const url = this.surveyApi
      .createUrl()
      .addRouteParam(customerNumber)
      .addRouteParam('participant')
      .addQueryParam('Country', country)
      .build();

    return (
      HttpClient.GetInstance.post<unknown, HypermediaObject>(url, eventInformation.payload)
        // .then(res => {
        //   console.log("response from server: ", res);
        //   HypermediaUtils.ParseResponseToObject<any>(res?.data)
        // })
        .then((res) => {
          return this._handleParticipantResponse(uniqueInteractionId, res?.data, stopBeforeModel);
        })
        .catch((error) => {
          console.error('Participant Response Error:', error);
          //return self._handleParticipantResponse(uniqueInteractionId);
        })
    );
    // const url = this._configuration.baseUrl + 'participant/';
    // return fetch(url, {
    //   method: 'POST',
    //   cache: 'no-cache',
    //   headers: this._configuration.headers,
    //   body: JSON.stringify(eventInformation.payload)
    // })
    // .then((response) => response.json())
    // .then((data) => {
    //   console.log('Participant Response:', data);
    //   return self._handleParticipantResponse(uniqueInteractionId, data);
    // })
    // .catch((error) => {
    //   console.error('Participant Response Error:', error);
    //   return self._handleParticipantResponse(uniqueInteractionId);
    // });
  }

  private _handleTriggerQueryResponse(
    //Handle response from trigger/query
    uniqueInteractionId: string,
    data: any = null,
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types
    startSurvey: boolean = false,
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types
    customerNumber: string = '',
    country?: string,
    stopBeforeModel = false
  ): any {
    const eventInformation = GenexModal._getEventInformation(
      this._configuration.sessionStorageKey,
      uniqueInteractionId
    );

    const errors = data && data.errors ? data.errors : null;
    eventInformation.triggerQueried = true;
    eventInformation.surveyId = data && data.result_ok ? data.survey_id : null;

    GenexModal._setEventInformation(this._configuration.sessionStorageKey, uniqueInteractionId, eventInformation);

    if (this._configuration.onTriggerResultEvent) {
      this._configuration.onTriggerResultEvent(uniqueInteractionId, eventInformation, errors);
    }

    if (startSurvey) {
      return this.startSurvey(uniqueInteractionId, customerNumber, null, country, stopBeforeModel); //call this again with the verified/updated eventInformation in mind.
    }

    return eventInformation;
  }

  private _handleParticipantResponse(
    //handle response from /participant
    uniqueInteractionId: string,
    data: any = null,
    stopBeforeModel = false
  ): any {
    const eventInformation = GenexModal._getEventInformation(
      this._configuration.sessionStorageKey,
      uniqueInteractionId
    );

    if (data && data.result_ok) {
      eventInformation.link = GenexModal._formatLink(data.link);
    }

    GenexModal._setEventInformation(this._configuration.sessionStorageKey, uniqueInteractionId, eventInformation);

    if (this._configuration.onBeforeSurveyStart) {
      this._configuration.onBeforeSurveyStart(uniqueInteractionId, eventInformation);
    }

    if(stopBeforeModel) {
      return {uniqueInteractionId, eventInformation};
    }

    this._handleSurveyModal(uniqueInteractionId, eventInformation);

    return eventInformation;
  }

  public loadIframe(uniqueInteractionId: string, url: string): void {
    const eventInformation = {
      link: url,
    };

    this._handleSurveyModal(uniqueInteractionId, eventInformation);
  }

  private _handleSurveyModal(uniqueInteractionId: string, eventInformation: any): void {
    //Does the heavy lifting of physically displaying the modal itself.
    const parentDiv = this._configuration.parentElement;
    const rootDiv = document.createElement('div');

    // backdrop
    const backdropDiv = document.createElement('div');
    backdropDiv.classList.add(this._configuration.backdropClass);

    if (this._configuration.backdropClickClose) {
      backdropDiv.addEventListener('click', (event) => {
        this._handleModalClose(uniqueInteractionId, rootDiv, event);
      });
    }

    rootDiv.appendChild(backdropDiv);

    // modal
    const modalDiv = document.createElement('div');
    modalDiv.classList.add(this._configuration.modalClass);
    rootDiv.appendChild(modalDiv);

    // modal close button
    const modalClose = document.createElement('div');
    modalClose.classList.add(this._configuration.modalCloseClass);
    modalClose.addEventListener('click', (event) => {
      this._handleModalClose(uniqueInteractionId, rootDiv, event);
    });

    const icon = document.createElement('i');
    icon.classList.add('icon-close');
    modalClose.appendChild(icon);

    modalDiv.appendChild(modalClose);

    // modal iframe
    const modalIFrame = document.createElement('iframe');
    modalIFrame.classList.add(this._configuration.modalIFrameClass);
    modalIFrame.setAttribute('border', '0');
    modalIFrame.setAttribute('src', eventInformation.link);
    window.addEventListener('message', (event) => {
      if (parentDiv.contains(rootDiv) && event.data === 'close-genex-modal') {
        this._handleModalClose(uniqueInteractionId, rootDiv, event);
      }
    });
    modalDiv.appendChild(modalIFrame);

    parentDiv.insertBefore(rootDiv, parentDiv.firstChild);

    if (this._configuration.onSurveyStarted) {
      this._configuration.onSurveyStarted(eventInformation);
    }
  }

  private _handleModalClose(uniqueInteractionId: string, rootDiv: HTMLElement, event: Event): void {
    const eventInformation = GenexModal._getEventInformation(
      this._configuration.sessionStorageKey,
      uniqueInteractionId
    );

    const parentDiv = this._configuration.parentElement;
    parentDiv.removeChild(rootDiv);

    if (this._configuration.onSurveyClosed) {
      this._configuration.onSurveyClosed(uniqueInteractionId, eventInformation);
    }
  }

  private static _formatLink(link: string): string {
    if (link.toLowerCase().startsWith('http://') || link.toLowerCase().startsWith('https://')) {
      return link;
    }

    return 'https://' + link;
  }

  private static _newEventInformation(sessionStorageKey: string, uniqueInteractionId: string): any {
    const sessionKey = sessionStorageKey + uniqueInteractionId;

    if (sessionStorage.getItem(sessionKey)) {
      return null;
    }

    const eventInformation = {
      triggerQueried: false,
      surveyActioned: false,
    };

    GenexModal._setEventInformation(sessionStorageKey, uniqueInteractionId, eventInformation);

    return eventInformation;
  }

  private static _setEventInformation(
    sessionStorageKey: string,
    uniqueInteractionId: string,
    eventInformation: any
  ): void {
    const sessionKey = sessionStorageKey + uniqueInteractionId;
    sessionStorage.setItem(sessionKey, JSON.stringify(eventInformation));
  }

  private static _getEventInformation(sessionStorageKey: string, uniqueInteractionId: string): any {
    const sessionKey = sessionStorageKey + uniqueInteractionId;

    if (!sessionStorage.getItem(sessionKey)) {
      return null;
    }

    const sessionData = sessionStorage.getItem(sessionKey);
    const eventInformation = sessionData ? JSON.parse(sessionData) : null;

    return eventInformation;
  }
}